/** @format */

import React, { useRef, forwardRef, useImperativeHandle, useState } from 'react'
import {
  _$,
  Form,
  FormItem,
  Select,
  Option,
  Input,
  Timepicker,
  Icon,
  Tooltip,
  OptionGroup,
  Divider,
  Switch,
  Tag
} from '@king-design/react'
import styled from 'styled-components'
import { TablePage } from 'src/components'
import { cloneDeep } from 'lodash'
import ApiCommon from 'src/api/ApiCommon'
import { capitalizeFirst, handleParamsCase, request } from 'src/utils'
import { useAsync } from 'react-use'
import {
  TriggerRulesProps,
  periodOption,
  methodOption,
  unitOption,
  pointsOption,
  intervalOption,
  countOption,
  initRuleItem,
  withCustomRule,
  thanLastPeriod,
  getMetricUnitOptions,
  handleUnitsValue,
  bps_ops,
  LevelValueItemProps
} from './constants'
import variable from 'src/static/styles/var'
import UnitSelect from './components/UnitSelect'
import EditLevel from './components/EditLevel'

const availableMemoryMetricNames = ['可用内存', 'memory availability']
const availableMemoryRateMetricNames = ['内存可用率', 'available memory']

interface AlarmRuleProps {
  className?: string
  productType?: number
  triggerRules?: TriggerRulesProps[]
  showAlertGrading?: boolean // 是否显示告警分级
  alertGrading?: boolean // 告警分级
  setAlertGrading?: (value: boolean) => void
  setTriggerRules?: (value: TriggerRulesProps[]) => void
  hidden?: boolean
  required?: boolean
  readonly?: boolean
}
export interface AlarmRuleHandle {
  getTriggerRules: (alertGrading?: boolean) => Promise<TriggerRulesProps[]>
}

const LevelTypeColor = {
  Critical: 'danger',
  Error: 'warning',
  Warning: 'primary'
}

const StyledAlarmRuleWrapper = styled.div`
  .alarm_rule_title {
    display: flex;
    height: 20px;
    align-items: center;
    margin-top: 16px;
    & > * {
      margin-bottom: 0 !important;
    }
    .create_model_title {
      padding: 0 !important;
      margin-top: 0 !important;
      border-top: none !important;
    }
    p {
      line-height: 12px;
      font-size: 12px;
    }
    a {
      line-height: 12px;
      font-size: 12px;
      .k-icon {
        color: #0091ea;
        font-size: 14px;
        margin-right: 4px;
      }
    }
    .alarm_rule_level {
      display: flex;
      align-items: center;
      p {
        margin-bottom: 0;
        margin-right: 4px;
      }
    }
  }
  .k-table {
    .ion-help-circled {
      font-size: 14px;
      margin-left: 4px;
    }
    .table-space {
      margin: 0;
      .k-content > * {
        margin-right: 4px;
      }
    }
    .k-form-item .k-form-error {
      position: static;
      .c-ellipsis {
        white-space: normal;
      }
    }
    .trigger_value_box {
      display: flex;
      align-items: center;
      .level_value {
        display: flex;
        align-items: center;
        & > * {
          margin: 2px 4px;
        }
        .k-icon {
          cursor: pointer;
        }
      }
    }
  }
  .alarm_rule_table {
    .lavel_value_item {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      p {
        margin-bottom: 0;
        margin-right: 4px;
      }
      &:nth-child(1) {
        p {
          color: ${variable.errorColor};
        }
      }
      &:nth-child(2) {
        p {
          color: ${variable.warningColor};
        }
      }
      &:nth-child(3) {
        margin-bottom: 0px;
        p {
          color: ${variable.primaryColor};
        }
      }
    }
  }
`

const AlarmRule = forwardRef<AlarmRuleHandle, AlarmRuleProps>(
  (
    {
      triggerRules = [],
      setTriggerRules,
      productType,
      hidden,
      required,
      readonly,
      showAlertGrading,
      alertGrading,
      setAlertGrading
    },
    ref
  ) => {
    const formRef = useRef(null)
    const [metricGroup, setMetricGroup] = useState([])
    const { value: metricList } = useAsync(async () => {
      if (productType !== undefined) {
        const { data } = await request(ApiCommon.getProductMetricList, {
          data: { ProductType: productType, PageIndex: 1 }
        })
        if (data?.metricList?.length) {
          const result = []
          data?.metricList.forEach(
            ({ application, metricKey, metricName, units, delay, isDimension }) => {
              const key = application || _$('其它')
              const findIndex = result.findIndex(({ app }) => app === key)
              if (findIndex >= 0) {
                result[findIndex].children.push({
                  metricKey,
                  metricName,
                  units,
                  delay,
                  isDimension
                })
              } else {
                result.push({
                  app: key,
                  children: [{ metricKey, metricName, units, delay, isDimension }]
                })
              }
            }
          )
          setMetricGroup(result)
        }
        return data?.metricList || []
      }
      return []
    }, [productType])
    // 修改告警规则
    const changeRules = (index, value) => {
      const update = cloneDeep(triggerRules)
      update[index] = { ...update[index], ...value }
      setTriggerRules && setTriggerRules(update)
    }

    // 添加告警规则
    const addTriggerRules = async () => {
      const valid = await formRef.current.validate()
      if (valid) {
        const add = cloneDeep(triggerRules)
        add.push(initRuleItem)
        setTriggerRules && setTriggerRules(add)
      }
    }

    // 删除告警规则
    const removeTriggerRules = index => {
      const remove = cloneDeep(triggerRules)
      remove.splice(index, 1)
      setTriggerRules && setTriggerRules(remove)
    }

    useImperativeHandle(ref, () => ({
      getTriggerRules: async alertGrading => {
        const valid = await formRef.current.validate()
        return valid
          ? triggerRules.map(item => {
              const { itemKey, triggerValue, units, displayUnits, levelValue } = item
              const unitOpts = getMetricUnitOptions(productType, itemKey, units)
              let value = triggerValue
              if (unitOpts?.length) {
                value = handleUnitsValue(triggerValue, displayUnits, units, unitOpts)
              } else if (units === 'bps') {
                value = handleUnitsValue(triggerValue, displayUnits, units, bps_ops)
              }
              const hierarchicalValue = {}
              if (alertGrading) {
                Object.entries(levelValue).forEach(([key, item]) => {
                  const { Value, DisplayUnits, Units } = item as LevelValueItemProps
                  if (Value) {
                    let newValue = Value
                    if (unitOpts?.length) {
                      newValue = handleUnitsValue(Value, DisplayUnits, Units, unitOpts)
                    } else if (units === 'bps') {
                      newValue = handleUnitsValue(Value, DisplayUnits, Units, bps_ops)
                    }
                    hierarchicalValue[key] = {
                      Value: newValue + '',
                      Units: units,
                      DisplayUnits: DisplayUnits
                    }
                  }
                })
              }
              return {
                ...item,
                triggerValue: value + '',
                levelValue: alertGrading ? hierarchicalValue : undefined
              }
            })
          : valid
      }
    }))

    const scheme: any = {
      itemName: {
        title: _$('监控项名称'),
        width: 300,
        fixed: 'left',
        template({ itemKey, itemName, TriggerValue }, index) {
          return readonly ? (
            itemName
          ) : (
            <FormItem
              className="table-space"
              hideLabel={true}
              value={itemKey && itemName ? `${itemKey}--${itemName}` : ''}
              rules={{ required: true }}
            >
              <Select
                style={{ width: '255px' }}
                card
                value={itemKey && itemName ? `${itemKey}--${itemName}` : ''}
                filterable
                className="metrci_select"
                container=".select_position"
                placeholder={_$('选择监控项')}
                onChangeValue={value => {
                  const split = value.split('--')
                  const find = metricList?.find(
                    item => item.metricKey === split[0] && item.metricName === split[1]
                  )
                  const unitOpts = getMetricUnitOptions(productType, split[0], find?.units)
                  let initUnit = find.units
                  if (unitOpts?.length) {
                    if (unitOpts.includes('Mbps')) {
                      initUnit = 'Mbps'
                    }
                  } else if (find.units === 'bps') {
                    initUnit = 'Mbps'
                  }
                  if (find) {
                    changeRules(index, {
                      itemKey: find.metricKey,
                      itemName: find.metricName,
                      units: find.units,
                      displayUnits: initUnit,
                      levelValue: {
                        Critical: {
                          Value: '',
                          Units: find.units,
                          DisplayUnits: initUnit
                        },
                        Error: {
                          Value: TriggerValue || '0',
                          Units: find.units,
                          DisplayUnits: initUnit
                        },
                        Warning: {
                          Value: '',
                          Units: find.units,
                          DisplayUnits: initUnit
                        }
                      }
                    })
                  }
                }}
              >
                {metricGroup?.length
                  ? metricGroup.map(({ app, children }, index) => (
                      <OptionGroup label={app} key={`${app}-${index}`}>
                        {children.map(item => (
                          <Option
                            key={`${item.metricKey}`}
                            label={item.metricName}
                            value={`${item.metricKey}--${item.metricName}`}
                          >
                            <div className="metric_option">
                              <p>{item.metricName}</p>
                              <p className="metric_key">{item.metricKey}</p>
                            </div>
                          </Option>
                        ))}
                      </OptionGroup>
                    ))
                  : null}
              </Select>
              {availableMemoryMetricNames.includes(itemName) ? (
                <Tooltip
                  content={
                    // eslint-disable-next-line max-len
                    _$(
                      '从/proc/meminfo中得到MemAvailable，若/proc/meminfo中无MemAvailable，则MemAvailable=MemFree+Buffers+Cached'
                    )
                  }
                >
                  <Icon className="icon ion-help-circled" />
                </Tooltip>
              ) : null}
              {availableMemoryRateMetricNames.includes(itemName) ? (
                <Tooltip content={_$('可用内存/总内存*100%')}>
                  <Icon className="icon ion-help-circled" />
                </Tooltip>
              ) : null}
            </FormItem>
          )
        }
      },
      period: {
        title: _$('统计周期'),
        width: 150,
        template({ itemKey, period, compare }, index) {
          if (withCustomRule.includes(itemKey)) return ''
          return readonly ? (
            _$(periodOption.find(item => item.value === period)?.title || '')
          ) : (
            <FormItem hideLabel={true} value={period || ''} rules={{ required: true }}>
              <Select
                width={130}
                value={period || ''}
                container=".select_position"
                onChangeValue={value => {
                  changeRules(index, {
                    period: value
                  })
                }}
              >
                {periodOption?.length
                  ? periodOption.map(item => {
                      if (thanLastPeriod.includes(compare) && item.value === '60m') {
                        return null
                      } else {
                        return (
                          <Option key={item.key} value={item.value}>
                            {item.title}
                          </Option>
                        )
                      }
                    })
                  : ''}
              </Select>
            </FormItem>
          )
        }
      },
      method: {
        title: _$('统计方法'),
        width: 150,
        template({ itemKey, method }, index) {
          if (withCustomRule.includes(itemKey)) return ''
          return readonly ? (
            _$(methodOption.find(item => item.value === method)?.title || '')
          ) : (
            <FormItem hideLabel={true} value={method || ''} rules={{ required: true }}>
              <Select
                width={130}
                value={method || ''}
                container=".select_position"
                onChangeValue={value => {
                  changeRules(index, {
                    method: value
                  })
                }}
              >
                {methodOption?.length
                  ? methodOption.map(item => (
                      <Option key={item.value} value={item.value}>
                        {item.title}
                      </Option>
                    ))
                  : ''}
              </Select>
            </FormItem>
          )
        }
      },
      compare: {
        title: _$('大小'),
        width: 180,
        template({ itemName, itemKey, period, compare }, index) {
          if (withCustomRule.includes(itemKey) || itemKey?.includes('.nodata')) return ''
          return readonly ? (
            _$(unitOption.find(item => item.value === compare)?.title || '')
          ) : (
            <FormItem hideLabel={true} value={compare || ''} rules={{ required: true }}>
              <Select
                width={160}
                value={compare || ''}
                container=".select_position"
                onChangeValue={value => {
                  const units = thanLastPeriod.includes(value)
                    ? '%'
                    : metricList?.find(item => {
                        return item.metricKey === itemKey && item.metricName === itemName
                      })?.units || ''
                  // 大小为【周期环比】没有1小时选项
                  changeRules(index, {
                    compare: value,
                    units,
                    period: thanLastPeriod.includes(value) && period === '60m' ? '' : period,
                    displayUnits: thanLastPeriod.includes(value)
                      ? '%'
                      : units === 'bps'
                      ? 'Mbps'
                      : units
                  })
                }}
              >
                {unitOption?.length
                  ? unitOption.map(item => (
                      <Option key={item.value} value={item.value}>
                        {item.title}
                      </Option>
                    ))
                  : ''}
              </Select>
            </FormItem>
          )
        }
      },
      triggerValue: {
        title: _$('阈值'),
        width: 250,
        template(ruleItem, index) {
          const { itemKey, triggerValue = 0, levelValue, units, compare, displayUnits } = ruleItem
          if (withCustomRule.includes(itemKey) || itemKey?.includes('.nodata')) return ''
          const unitOpts = getMetricUnitOptions(productType, itemKey, units)
          if (readonly) {
            if (alertGrading) {
              return (
                <div className="ksc-space">
                  {Object.entries(levelValue)?.map(([key, item]) => {
                    const { Value, DisplayUnits, Units } = handleParamsCase(
                      item
                    ) as LevelValueItemProps
                    const type = LevelTypeColor[capitalizeFirst(key)]
                    return Value ? (
                      <Tag type={type} key={key}>
                        {Value}
                        {DisplayUnits || Units || ''}
                      </Tag>
                    ) : null
                  })}
                </div>
              )
            } else {
              return `${triggerValue}${displayUnits || units || ''}`
            }
          }
          return (
            <div className="trigger_value_box">
              {alertGrading ? (
                <FormItem
                  className="table-space"
                  hideLabel={true}
                  value={levelValue}
                  rules={{ ObjectLength: true }}
                >
                  {itemKey ? (
                    <div className="ksc-space level_value">
                      {Object.entries(levelValue).map(([key, item]) => {
                        const { Value, DisplayUnits, Units } = item as LevelValueItemProps
                        const type = LevelTypeColor[key]
                        return Value ? (
                          <Tag
                            type={type}
                            key={`${itemKey}-${key}`}
                            closable
                            onClose={() => {
                              const newLevelValue = cloneDeep(levelValue)
                              newLevelValue[key].Value = undefined
                              changeRules(index, {
                                levelValue: newLevelValue
                              })
                            }}
                          >
                            {Value}
                            {DisplayUnits || Units || ''}
                          </Tag>
                        ) : null
                      })}

                      <EditLevel
                        ruleItem={ruleItem}
                        unitOpts={unitOpts}
                        onChange={value => {
                          changeRules(index, {
                            levelValue: value
                          })
                        }}
                      />
                    </div>
                  ) : null}
                </FormItem>
              ) : (
                <FormItem
                  className="table-space"
                  hideLabel={true}
                  value={triggerValue}
                  rules={{ required: true, pointsLimit: true }}
                >
                  <Input
                    onChangeValue={value =>
                      changeRules(index, {
                        triggerValue: value
                      })
                    }
                    className={!unitOpts?.length ? 'value_input' : ''}
                    width={itemKey === 'vpc.nat.ipconflict' ? 120 : 230}
                    value={triggerValue}
                    slotAppend={
                      itemKey ? (
                        <UnitSelect
                          unitOpts={unitOpts}
                          compare={compare}
                          units={units}
                          displayUnits={displayUnits}
                          onChange={value => {
                            changeRules(index, {
                              displayUnits: value
                            })
                          }}
                        />
                      ) : undefined
                    }
                  />
                  {itemKey === 'vpc.nat.ipconflict' ? (
                    // eslint-disable-next-line max-len
                    <Tooltip
                      content={_$(
                        '该计数为NAT时随机选择端口可能产生的冲突计数，仅当统计周期内，大量冲突计数产生，表明NAT端口资源紧张，建议阈值调整为每分钟200'
                      )}
                    >
                      <Icon className="icon ion-help-circled" />
                    </Tooltip>
                  ) : null}
                </FormItem>
              )}
            </div>
          )
        }
      },
      effectBT: {
        title: _$('告警生效时间'),
        width: 160,
        template({ itemKey, effectBT, effectET }, index) {
          if (withCustomRule.includes(itemKey)) return ''
          return readonly ? (
            `${effectBT}-${effectET}`
          ) : (
            <FormItem
              hideLabel={true}
              value={effectBT && effectET ? [effectBT, effectET] : []}
              rules={{ required: true }}
            >
              <Timepicker
                style={{ width: '140px' }}
                value={[effectBT, effectET]}
                onChangeValue={time => {
                  changeRules(index, {
                    effectBT: time[0],
                    effectET: time[1]
                  })
                }}
                range
                valueFormat="HH:mm"
                showFormat="HH:mm"
              />
            </FormItem>
          )
        }
      },
      points: {
        title: (
          <span>
            {_$('持续周期数')}
            <Tooltip content={_$('连续几次报警的触发结果符合您设置的规则描述，才会触发报警')}>
              <Icon className="icon ion-help-circled" />
            </Tooltip>
          </span>
        ),
        width: 120,
        template({ itemKey, period, points }, index) {
          if (withCustomRule.includes(itemKey)) return ''
          return readonly ? (
            points
          ) : (
            <FormItem hideLabel={true} value={points || ''} rules={{ required: true }}>
              <Select
                width={100}
                value={points || ''}
                container=".select_position"
                onChangeValue={value => {
                  changeRules(index, {
                    points: value
                  })
                }}
              >
                {pointsOption?.length
                  ? pointsOption
                      .filter(({ value }) => value * parseInt(period) <= 60)
                      .map(item => (
                        <Option key={item.value} value={item.value}>
                          {item.title}
                        </Option>
                      ))
                  : ''}
              </Select>
            </FormItem>
          )
        }
      },
      interval: {
        title: (
          <span>
            {_$('告警间隔')}
            <Tooltip content={_$('报警发生后如果未恢复正常，间隔多久后重复发送一次报警通知')}>
              <Icon className="icon ion-help-circled" />
            </Tooltip>
          </span>
        ),
        width: 120,
        template({ interval }, index) {
          return readonly ? (
            _$(intervalOption.find(item => item.value === interval)?.title || '')
          ) : (
            <FormItem hideLabel={true} value={interval || ''} rules={{ required: true }}>
              <Select
                width={100}
                value={interval || ''}
                container=".select_position"
                onChangeValue={value => {
                  changeRules(index, {
                    interval: value
                  })
                }}
              >
                {intervalOption?.length
                  ? intervalOption.map(item => (
                      <Option key={item.value} value={item.value}>
                        {item.title}
                      </Option>
                    ))
                  : null}
              </Select>
            </FormItem>
          )
        }
      },
      maxCount: {
        title: (
          <span>
            {_$('告警次数')}
            <Tooltip content={_$('发送告警通知的最大次数')}>
              <Icon className="icon ion-help-circled" />
            </Tooltip>
          </span>
        ),
        width: 120,
        template({ maxCount }, index) {
          return readonly ? (
            `${maxCount}次`
          ) : (
            <FormItem hideLabel={true} value={maxCount || ''} rules={{ required: true }}>
              <Select
                width={100}
                value={maxCount || ''}
                container=".select_position"
                onChangeValue={value => {
                  changeRules(index, {
                    maxCount: value
                  })
                }}
              >
                {countOption?.length
                  ? countOption.map(item => (
                      <Option key={item.value} value={item.value}>
                        {item.title}
                        {_$('次')}
                      </Option>
                    ))
                  : ''}
              </Select>
            </FormItem>
          )
        }
      }
    }
    if (!readonly) {
      scheme.active = {
        title: _$('操作'),
        fixed: 'right',
        width: 100,
        template(_item, index) {
          return required && triggerRules?.length <= 1 ? (
            ''
          ) : (
            <a
              onClick={() => {
                removeTriggerRules(index)
              }}
            >
              {_$('删除')}
            </a>
          )
        }
      }
    }

    return (
      <StyledAlarmRuleWrapper style={{ display: hidden ? 'none' : 'block' }}>
        {!readonly ? (
          <div className="alarm_rule_title">
            <p>{_$('满足任意条件可触发')}</p>
            <Divider type="vertical" />
            <a
              onClick={() => {
                addTriggerRules()
              }}
            >
              <Icon className="ion-plus-round" />
              {_$('新建规则')}
            </a>
            {showAlertGrading ? (
              <>
                <Divider type="vertical" />
                <div className="alarm_rule_level">
                  <p>{_$('告警分级')}</p>
                  <Switch
                    size="small"
                    value={alertGrading}
                    onChangeValue={value => {
                      setAlertGrading && setAlertGrading(value)
                    }}
                  />
                </div>
              </>
            ) : null}
          </div>
        ) : null}
        <Form ref={formRef} className="alarm_rule_table">
          <TablePage
            style={{ marginTop: 8 }}
            tableProps={{
              checkType: 'none',
              data: triggerRules,
              scheme: scheme,
              resizable: true,
              rowKey: item => item.ruleId || `${item.itemKey}--${item.itemName}`
            }}
          ></TablePage>
        </Form>
      </StyledAlarmRuleWrapper>
    )
  }
)

export default AlarmRule
